import { useContext, useEffect, useState, useRef, FC } from "react";
import "cesium/Build/Cesium/Widgets/widgets.css";
import Context from "../../common/components/utils/context";
import { Map } from "../../common/components/map/map";
import { IoMdMap } from "react-icons/io";
import { IoLocationOutline, IoSearch } from "react-icons/io5";
import { SearchV2Service } from "../../common/services/api/api";
import { IPost } from "../posts/models";
import { Loading } from "../../common/components/loading/loading";
import PostView from "../posts/postView/postView";
import { Themes } from "../../common/enums/themes";
import { Circles } from "react-loader-spinner";
import { IMapPin } from "../../common/components/map/models";
import { Results } from "./searchResults/searchResults";
import { Viewer } from "resium";
import {
  Cartesian2,
  Cartesian3,
  Cartographic,
  Math as CesiumMath,
  Viewer as CesiumViewer,
} from "cesium";

export const Search = () => {
  const context = useContext(Context);
  const form = useRef() as React.LegacyRef<HTMLFormElement>;
  const [dirty, setDirty] = useState<boolean>(false);
  const [tab, setTab] = useState<number>(4);
  const [results, setResults] = useState<IPost[]>();
  const [locations, setLocations] = useState<IMapPin[]>([]);
  const [selected, setSelected] = useState<string>();
  const [postSelected, setPostSelected] = useState<string>();
  const [pinSelected, setPinSelected] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [showPins, setShowPins] = useState<boolean>(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [query, setQuery] = useState<string | undefined>();
  const [bodyText, setBodyText] = useState<string>("");
  const [lat, setLat] = useState<string>("");
  const [lng, setLng] = useState<string>("");
  const [distance, setDistance] = useState(10);
  const [description, setDescription] = useState<string>("");
  const [username, setUsername] = useState<string>("");
  const [status, setStatus] = useState<string | undefined>();
  const [discipline, setDiscipline] = useState<string>("");
  const [postDateFrom, setPostDateFrom] = useState<string>("");
  const [postDateTo, setPostDateTo] = useState<string>("");
  const [isSearchByImage, setIsSearchByImage] = useState(false);

  useEffect(() => {
    if ((results?.length ?? -1) >= 0) {
      setTab(2);
      setShowPins(true);
      results?.map((result) => {
        const location: IMapPin = {
          id: result.id,
          latitude: result.latitude,
          longitude: result.longitude,
          altitude: result.altitude,
        };
        setLocations((locations) => [...locations, location]);
      });
    }
  }, [results]);

  useEffect(() => {
    if (selected) {
      context.setModal({
        show: true,
        component: <PostView postId={selected} modal={true} isSearch={true} />,
        size: "md",
        onClose: () => context.setModal({ ...context.modal, show: false }),
        outsideClickToClose: true,
      });
    }
  }, [selected]);

  const quickSearch = async (pageNumber = 1) => {
    setShowPins(false);
    setLocations([]);
    setDirty(true);
    setLoading(true);
    const queryTextTrimmed = (query ?? "").trim();
    const data: { [key: string]: string } = {
      body: queryTextTrimmed,
      page: pageNumber.toString(),
    };
    try {
      const searchService = new SearchV2Service();
      const response = await searchService.advanced(data);
      if (pageNumber === 1) {
        setResults(response.data.items);
      } else {
        setResults((prevResults) => [
          ...(prevResults || []),
          ...(response.data.items || []),
        ]);
      }
      setPage(pageNumber);
      setHasMore(response.data.hasNextPage === true);
      setDirty(false);
      setLoading(false);
    } catch (error: any) {
      console.error("Error fetching posts:", error);
      if (error.status === 404) {
        setResults([]);
        setHasMore(false);
      }
      setDirty(false);
      setLoading(false);
    }
  };

  const submit = async (
    event: React.FormEvent<HTMLFormElement>,
    pageNumber = 1
  ) => {
    event.preventDefault();
    setShowPins(false);
    setLocations([]);
    setDirty(true);
    setLoading(true);
    if (tab === 0 && (!lat || !lng)) {
      context.setAlert({
        message: "Please enter a valid location.",
        theme: Themes.Danger,
      });
      setDirty(false);
      setLoading(false);
      return;
    }
    const data: { [key: string]: string } =
      status === undefined
        ? {
            body: bodyText.trim(),
            latitude: lat,
            longitude: lng,
            distance: distance.toString(),
            page: pageNumber.toString(),
          }
        : {
            userName: username.trim(),
            body: description.trim(),
            discipline: discipline,
            status: status,
            startCreatedDate: postDateFrom,
            endCreatedDate: postDateTo,
            page: pageNumber.toString(),
          };
    try {
      const searchService = new SearchV2Service();
      const response =
        status === undefined
          ? await searchService.distance(data)
          : isSearchByImage ? await searchService.imageSearch(data) : await searchService.advanced(data);
      if (pageNumber === 1) {
        setResults(status === undefined ? response.data : response.data.items);
      } else {
        setResults((prevResults) => [
          ...(prevResults || []),
          ...((status === undefined ? response.data : response.data.items) ||
            []),
        ]);
      }
      setPage(pageNumber);
      setHasMore(response.data.hasNextPage === true);
      setHasMore(
        status === undefined
          ? response.data.length === 10
          : response.data.hasNextPage === true
      );
      setDirty(false);
      setLoading(false);
    } catch (error: any) {
      console.error("Error fetching posts:", error);
      if (error.status === 404) {
        setResults([]);
        setHasMore(false);
      }
      setDirty(false);
      setLoading(false);
    }
  };

  const toggleMyLocation = () => {
    if (navigator.geolocation) {
      setLoading(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLat(position.coords.latitude.toString());
          setLng(position.coords.longitude.toString());
          setLoading(false);
        },
        (error) => {
          context.setAlert({
            message: `Error getting location: ${error.message}`,
            theme: Themes.Danger,
          });
          console.error(`Error getting location: ${error.message}`);
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0,
        }
      );
    } else {
      context.setAlert({
        message: "Geolocation not supported.",
        theme: Themes.Warning,
      });
      console.error("Geolocation not supported!");
    }
  };

  const sendData = (data: any) => {
    setLat(data.lat);
    setLng(data.lng);
    setTab(0);
  };

  return (
    <main className="h-full w-full flex mb-auto relative overflow-y-scroll">
      <Map
        locations={locations}
        showPins={showPins}
        selected={setPostSelected}
        setPostSelected={setPostSelected}
        flyTo={postSelected}
        viewportLatitude={(latitude) => setLat(latitude.toString())}
        viewportLongitude={(longitude) => setLng(longitude.toString())}
        onPinClick={setPinSelected}
        className="w-2/3"
      />
      <div className="h-full w-1/3 overflow-y-scroll">
        {tab === 4 && (
          <label
            htmlFor="body"
            className="text-gray-500 text-sm w-full inline-flex items-center"
          >
            <input
              type="search"
              className="block bg-gray-50 w-full p-2 ms-2 me-1 mt-1 mb-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-300 text-sm"
              id="body"
              name="body"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search"
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  setResults(undefined);
                  quickSearch();
                }
              }}
            />
            <button type="button" className="ms-0 pe-2 pb-1">
              <IoSearch
                size={34}
                className="text-gray-400 bg-cyan-300 hover:text-gray-800 cursor-pointer rounded-md"
                onClick={() => {
                  setResults(undefined);
                  quickSearch();
                }}
              />
            </button>
          </label>
        )}
        <form onSubmit={submit} ref={form}>
          <fieldset className="border h-full rounded-lg bg-gray-50 w-full">
            <div className="cursor-pointer px-4 py-1 justify-between items-center">
              <div className="pt-0">
                {!loading && (
                  <div className="grid grid-flow-col justify-stretch">
                    {tab === 2 ? (
                      <button
                        type="button"
                        className={`${
                          tab === 2
                            ? "text-gray-900 border-text-link"
                            : "text-gray-400 border-gray-300"
                        } border-b-2 tracking-wider font-medium outline-none hover:bg-gray-100 px-10 py-2`}
                      >
                        Results
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          className={`${
                            tab === 0
                              ? "text-gray-900 border-text-link"
                              : "text-gray-400 border-gray-300"
                          } border-b-2 tracking-wider font-medium outline-none hover:bg-gray-100 px-10 py-2`}
                          onClick={() => {
                            setTab(0);
                          }}
                        >
                          Distance
                        </button>
                        <button
                          type="button"
                          className={`${
                            tab === 1
                              ? "text-gray-900 border-text-link"
                              : "text-gray-400 border-gray-300"
                          } border-b-2 tracking-wider font-medium outline-none hover:bg-gray-100 px-10 py-2`}
                          onClick={() => {
                            setTab(1);
                          }}
                        >
                          Advanced
                        </button>
                      </>
                    )}
                  </div>
                )}
                <div className="p-4">
                  {loading && <Loading />}
                  {tab === 0 && (
                    <DistanceSearch
                      bodyText={bodyText}
                      longitude={lng}
                      latitude={lat}
                      distance={distance}
                      onChange={(newBody, newLng, newLat, newDistance) => {
                        setBodyText(newBody);
                        setLng(newLng);
                        setLat(newLat);
                        setDistance(newDistance);
                      }}
                      toggleMyLocation={toggleMyLocation}
                      toggleMapLocation={() => {
                        setTab(3);
                      }}
                      cancel={() => {
                        setTab(4);
                      }}
                    />
                  )}
                  {tab === 1 && (
                    <AdvancedSearch
                      description={description}
                      username={username}
                      discipline={discipline}
                      status={status}
                      postDateFrom={postDateFrom}
                      postDateTo={postDateTo}
                      isSearchByImage={isSearchByImage}
                      onChange={(
                        newDesc,
                        newUser,
                        newStatus,
                        newDisc,
                        newFrom,
                        newTo,
                        newIsSearchByImage
                      ) => {
                        setDescription(newDesc);
                        setUsername(newUser);
                        setDiscipline(newDisc);
                        setStatus(newStatus);
                        setPostDateFrom(newFrom);
                        setPostDateTo(newTo);
                        setIsSearchByImage(newIsSearchByImage);
                      }}
                      cancel={() => {
                        setTab(4);
                      }}
                    />
                  )}
                  {tab === 2 && (
                    <>
                      <Results
                        posts={results}
                        setPostSelected={setPostSelected}
                        postSelected={postSelected}
                        pinSelected={pinSelected}
                        setPinSelected={setPinSelected}
                        showPost={setSelected}
                      />
                      <div className="grid grid-flow-col space-x-3 px-20 mt-10">
                        <button
                          hidden={!hasMore}
                          disabled={dirty}
                          onClick={(ev) => {
                            ev.preventDefault();
                            if (query) {
                              quickSearch(page + 1);
                            } else {
                              submit(
                                new SubmitEvent(
                                  "submit"
                                ) as unknown as React.FormEvent<HTMLFormElement>,
                                page + 1
                              );
                            }
                          }}
                          className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
                        >
                          <div className="flex justify-between items-center w-full">
                            <span>Load more...</span>
                            {dirty && (
                              <div className="mr-2">
                                <Circles
                                  height="20"
                                  width="20"
                                  color="#2DE1FC"
                                />
                              </div>
                            )}
                          </div>
                        </button>
                      </div>
                      <div className="grid grid-flow-col space-x-3 px-20 mt-10">
                        <button
                          type="button"
                          className="rounded-lg text-gray-50 bg-neutral-500 border border-gray-50 text-sm tracking-wider font-medium outline-none hover:bg-gray-900 px-10 py-2"
                          onClick={() => {
                            setTab(4);
                            setPage(1);
                            setResults(undefined);
                            setShowPins(false);
                            setQuery(undefined);
                            setBodyText("");
                            setLng("");
                            setLat("");
                            setDistance(10);
                            setDescription("");
                            setUsername("");
                            setStatus(undefined);
                            setDiscipline("");
                            setPostDateFrom("");
                            setPostDateTo("");
                          }}
                        >
                          Reset
                        </button>
                      </div>
                    </>
                  )}
                  {tab === 3 && (
                    <FixedPinMap
                      latitude={lat?.toString()}
                      longitude={lng?.toString()}
                      cancel={() => {
                        setTab(0);
                      }}
                      sendData={sendData}
                    />
                  )}
                </div>
                <div className="grid grid-flow-col px-20 mb-5">
                  <button
                    type="submit"
                    className="rounded-lg text-gray-900 bg-text-link text-sm tracking-wider font-medium outline-none hover:bg-gray-100 px-10 py-2"
                    disabled={dirty}
                    hidden={tab === 4 || tab === 2 || tab === 3}
                  >
                    Search
                  </button>
                </div>
              </div>
            </div>
          </fieldset>
        </form>
      </div>
    </main>
  );
};

export const AdvancedSearch: FC<IAdvancedSearch> = (props) => {
  const [isSearchByImage, setIsSearchByImage] = useState(false);

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    props.onChange?.(
      e.target.value,
      props.username ?? "",
      props.status ?? "",
      props.discipline ?? "",
      props.postDateFrom ?? "",
      props.postDateTo ?? "",
      props.isSearchByImage ?? false
    );
  };
  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    props.onChange?.(
      props.description ?? "",
      e.target.value,
      props.status ?? "",
      props.discipline ?? "",
      props.postDateFrom ?? "",
      props.postDateTo ?? "",
      props.isSearchByImage ?? false
    );
  };
  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    props.onChange?.(
      props.description ?? "",
      props.username ?? "",
      e.target.value,
      props.discipline ?? "",
      props.postDateFrom ?? "",
      props.postDateTo ?? "",
      props.isSearchByImage ?? false
    );
  };
  const handleDisciplineChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    props.onChange?.(
      props.description ?? "",
      props.username ?? "",
      props.status ?? "",
      e.target.value,
      props.postDateFrom ?? "",
      props.postDateTo ?? "",
      props.isSearchByImage ?? false
    );
  };
  const handlePostDateFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    props.onChange?.(
      props.description ?? "",
      props.username ?? "",
      props.status ?? "",
      props.discipline ?? "",
      e.target.value,
      props.postDateTo ?? "",
      props.isSearchByImage ?? false
    );
  };
  const handlePostDateToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    props.onChange?.(
      props.description ?? "",
      props.username ?? "",
      props.status ?? "",
      props.discipline ?? "",
      props.postDateFrom ?? "",
      e.target.value,
      props.isSearchByImage ?? false
    );
  };

  const handleSearchByImagesChange = (e: any) => {
    setIsSearchByImage(e.target.checked);
    props.onChange?.(
      props.description ?? "",
      props.username ?? "",
      props.status ?? "",
      props.discipline ?? "",
      props.postDateFrom ?? "",
      props.postDateTo ?? "",
      e.target.checked
    );
  };
  const handleReset = () => {
    props.onChange?.("", "", "", "", "", "", false);
  };
  const handleCancel = () => {
    if (props.cancel) {
      props.cancel();
    }
  };

  return (
    <>
      <div>
        <label className="inline-flex items-center me-5 cursor-pointer">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={isSearchByImage}
            onChange={handleSearchByImagesChange}
          />
          <div className="relative w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-4 peer-focus:ring-[#1ab9d8] dark:peer-focus:ring-[#2de1fc] dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-[#2de1fc]"></div>
          <span className="ms-3 text-sm font-medium text-gray-900 dark:text-gray-300">
            Search By Images
          </span>
        </label>
      </div>
      <label htmlFor="body" className="text-gray-500 text-sm">
        Description
        <input
          type="text"
          className="block w-full bg-gray-50 outline-none border-b py-2 px-4 mb-4"
          id="body"
          name="body"
          placeholder="Search Text..."
          value={props.description}
          onChange={handleDescriptionChange}
        />
      </label>
      <label htmlFor="username" className="text-gray-500 text-sm">
        Username
        <input
          type="text"
          className="block w-full bg-gray-50 outline-none border-b py-2 px-4 mb-4"
          id="username"
          name="username"
          value={props.username}
          onChange={handleUsernameChange}
        />
      </label>
      <label htmlFor="discipline" className="text-gray-500 text-sm">
        Discipline
        <select
          id="discipline"
          name="discipline"
          value={props.discipline}
          onChange={handleDisciplineChange}
          className="block w-full bg-gray-50 outline-none border-b py-2 px-4 mb-4"
        >
          <option value="null"></option>
          <option value="General">General</option>
          <option value="Design">Design</option>
          <option value="Safety">Safety</option>
          <option value="Civil">Civil</option>
          <option value="Structural">Structural</option>
          <option value="Mechanical">Mechanical</option>
          <option value="Hydraulic">Hydraulic</option>
          <option value="Electrical">Electrical</option>
          <option value="IT">IT</option>
          <option value="Communications">Communications</option>
          <option value="Audit">Audit</option>
          <option value="Compliance">Compliance</option>
          <option value="Inspection">Inspection</option>
        </select>
      </label>
      <label htmlFor="sharedWith" className="text-gray-500 text-sm">
        Shared with
        <select
          id="sharedWith"
          name="sharedWith"
          value={props.status}
          onChange={handleStatusChange}
          className="block w-full bg-gray-50 outline-none border-b py-2 px-4 mb-4"
        >
          <option value="null"></option>
          <option value="Public">Public</option>
          <option value="Me Only">Me Only</option>
          {/* <option value="All">All</option> */}
        </select>
      </label>
      <label htmlFor="startCreatedDate" className="text-gray-500 text-sm">
        Post date from
        <input
          type="date"
          className="block w-full bg-gray-50 outline-none border-b py-2 px-1 mb-4"
          id="startCreatedDate"
          name="startCreatedDate"
          value={props.postDateFrom}
          onChange={handlePostDateFromChange}
        />
      </label>
      <label htmlFor="endCreatedDate" className="text-gray-500 text-sm">
        Post date to
        <input
          type="date"
          className="block w-full bg-gray-50 outline-none border-b py-2 px-1 mb-4"
          id="endCreatedDate"
          name="endCreatedDate"
          value={props.postDateTo}
          onChange={handlePostDateToChange}
        />
      </label>
      <div className="grid grid-flow-col space-x-3 px-2 mt-10">
        <button
          type="button"
          className="rounded-lg text-gray-900 border border-gray-600 text-sm tracking-wider font-medium outline-none hover:bg-gray-100 px-10 py-2"
          onClick={handleReset}
        >
          Reset
        </button>
        <button
          type="button"
          className="rounded-lg text-gray-50 bg-neutral-500 border border-gray-50 text-sm tracking-wider font-medium outline-none hover:bg-gray-900 px-10 py-2"
          onClick={handleCancel}
        >
          Cancel
        </button>
      </div>
    </>
  );
};

export const DistanceSearch: FC<IDistanceSearch> = (props) => {
  const handleBodyTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    props.onChange?.(
      e.target.value,
      props.longitude ?? "",
      props.latitude ?? "",
      props.distance ?? 10
    );
  };
  const handleLongitudeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    props.onChange?.(
      props.bodyText ?? "",
      e.target.value,
      props.latitude ?? "",
      props.distance ?? 0
    );
  };
  const handleLatitudeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    props.onChange?.(
      props.bodyText ?? "",
      props.longitude ?? "",
      e.target.value,
      props.distance ?? 0
    );
  };
  const handleDistanceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    props.onChange?.(
      props.bodyText ?? "",
      props.longitude ?? "",
      props.latitude ?? "",
      parseInt(e.target.value)
    );
  };
  const handleReset = () => {
    props.onChange?.("", "", "", 10);
  };
  const handleCancel = () => {
    if (props.cancel) {
      props.cancel();
    }
  };
  return (
    <>
      <div className="flex justify-center mb-5">
        <div className="inline-flex items-stretch overflow-hidden rounded-lg border border-stroke dark:border-dark-3 w-full">
          <button
            type="button"
            className="border-r w-1/2 px-3 py-2 last-of-type:border-r-0 text-sm tracking-wider font-medium gap-x-2 flex items-center justify-center text-gray-800 bg-gray-100 shadow-inner cursor-default"
            onClick={props.toggleMyLocation}
          >
            <IoLocationOutline size={20} />
            <span>My location</span>
          </button>
          <button
            type="button"
            className="border-r w-1/2 px-3 py-2 last-of-type:border-r-0 text-sm tracking-wider font-medium gap-x-2 flex items-center justify-center text-gray-800 bg-gray-100 shadow-inner cursor-default"
            onClick={props.toggleMapLocation}
          >
            <IoMdMap size={20} />
            <span>Map location</span>
          </button>
        </div>
      </div>
      <label htmlFor="body" className="text-gray-500 text-sm">
        Description
        <input
          type="text"
          className="block w-full bg-gray-50 outline-none border-b py-2 px-4 mb-4"
          id="body"
          name="body"
          placeholder="Search Text..."
          value={props.bodyText}
          onChange={handleBodyTextChange}
        />
      </label>
      <label htmlFor="longitude" className="text-gray-500 text-sm">
        Longitude
        <input
          type="text"
          className="block w-full bg-gray-50 outline-none border-b py-2 px-4 mb-4"
          id="longitude"
          name="longitude"
          value={props.longitude}
          onChange={handleLongitudeChange}
        />
      </label>
      <label htmlFor="latitude" className="text-gray-500 text-sm">
        Latitude
        <input
          type="text"
          className="block w-full bg-gray-50 outline-none border-b py-2 px-4 mb-4"
          id="latitude"
          name="latitude"
          value={props.latitude}
          onChange={handleLatitudeChange}
        />
      </label>
      <label
        htmlFor="distance"
        className="text-gray-900 text-md block items-center font-sans text-sm font-normal w-full"
      >
        <p className="block text-gray-500 text-sm mb-4">
          Show result within:{" "}
          {(props.distance ?? 10) / ((props.distance ?? 10) < 1000 ? 1 : 1000)}
          {(props.distance ?? 10) < 1000 ? "m" : "km"}
        </p>
        <div className="relative h-3 w-full bg-gray-200 rounded-full mb-3">
          <span
            className="bg-[#2DE1FF] absolute h-full w-5 rounded-full z-0"
            style={{ width: `${((props.distance ?? 10) / 100).toString()}%` }}
          ></span>
          <input
            type="range"
            className="h-full w-full z-20 relative rounded-full appearance-none bg-transparent [&::-webkit-slider-thumb]:bg-[#2DE1FF] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:mt-[-6px] [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full"
            id="distance"
            name="distance"
            value={props.distance}
            onChange={handleDistanceChange}
            min="10"
            max="10000"
            step="1"
          />
        </div>
      </label>
      <div className="grid grid-flow-col space-x-3 px-2 mt-10">
        <button
          type="button"
          className="rounded-lg text-gray-900 border border-gray-600 text-sm tracking-wider font-medium outline-none hover:bg-gray-100 px-10 py-2"
          onClick={handleReset}
        >
          Reset
        </button>
        <button
          type="button"
          className="rounded-lg text-gray-50 bg-neutral-500 border border-gray-50 text-sm tracking-wider font-medium outline-none hover:bg-gray-900 px-10 py-2"
          onClick={handleCancel}
        >
          Cancel
        </button>
      </div>
    </>
  );
};

export interface IDistanceSearch {
  bodyText?: string;
  longitude?: string;
  latitude?: string;
  distance?: number;
  onChange?: (body: string, lng: string, lat: string, dis: number) => void;
  toggleMyLocation?: () => void;
  toggleMapLocation?: () => void;
  sendData?: (data: { lat: number; lng: number }) => void;
  cancel?: () => void;
}

export interface IAdvancedSearch {
  description?: string;
  username?: string;
  status?: string;
  discipline?: string;
  postDateFrom?: string;
  postDateTo?: string;
  isSearchByImage?: boolean;
  onChange?: (
    body: string,
    user: string,
    status: string,
    discip: string,
    from: string,
    to: string,
    isSearchByImage: boolean
  ) => void;
  cancel?: () => void;
}

export const FixedPinMap: FC<IDistanceSearch> = (props) => {
  const viewerRef = useRef<null | CesiumViewer>(null);
  const [coords, setCoords] = useState({
    lat: parseFloat(props.latitude || "0"),
    lng: parseFloat(props.longitude || "0"),
  });
  const sendData = () => {
    if (props.sendData) {
      props.sendData(coords);
    }
  };
  const handleCancel = () => {
    if (props.cancel) {
      props.cancel();
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      const viewer = viewerRef.current;
      if (!viewer) {
        return;
      }
      viewer.camera.flyTo({
        destination: Cartesian3.fromDegrees(coords.lng, coords.lat, 500),
      });
      const scene = viewer.scene;
      scene.postRender.addEventListener(updateCenterCoordinates);
      return () => {
        scene.postRender.removeEventListener(updateCenterCoordinates);
        clearTimeout(timeout);
      };
    }, 1000);
  }, []);

  const updateCenterCoordinates = () => {
    const viewer = viewerRef.current;
    if (!viewer) return;
    const scene = viewer.scene;
    const canvas = scene.canvas;
    const center = new Cartesian2(
      canvas.clientWidth / 2,
      canvas.clientHeight / 2
    );
    const ray = scene.camera.getPickRay(center);
    if (!ray) return;
    const cartesian = scene.globe.pick(ray, scene);
    if (cartesian) {
      const cartographic = Cartographic.fromCartesian(cartesian);
      const lat = CesiumMath.toDegrees(cartographic.latitude);
      const lng = CesiumMath.toDegrees(cartographic.longitude);
      setCoords({ lat, lng });
    }
  };
  return (
    <>
      <Viewer
        navigationHelpButton={false}
        geocoder={true}
        timeline={false}
        animation={false}
        fullscreenButton={false}
        style={{
          position: "absolute",
          top: "7%",
          left: "67%",
          right: "1%",
          bottom: "10%",
        }}
        ref={(e) => {
          if (e?.cesiumElement) viewerRef.current = e.cesiumElement;
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -100%)",
            pointerEvents: "none",
          }}
        >
          <img
            src="/img/pin-sharp.png"
            alt="pin"
            style={{ width: 36, height: 36 }}
          />
        </div>
        <div
          style={{
            position: "absolute",
            top: 45,
            left: 10,
            background: "white",
            padding: "6px 12px",
            borderRadius: 4,
            fontFamily: "monospace",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          }}
        >
          Lat: {coords.lat.toFixed(6)} <br />
          Lng: {coords.lng.toFixed(6)}
        </div>
      </Viewer>
      <div
        style={{
          position: "absolute",
          left: "66%",
          right: "2%",
          bottom: "-2%",
        }}
        className="grid grid-flow-col justify-stretch space-x-3 px-20 mb-5"
      >
        <button
          type="button"
          className="rounded-lg text-gray-900 border border-gray-600 text-sm tracking-wider font-medium outline-none hover:bg-gray-100 px-10 py-2"
          onClick={handleCancel}
        >
          Cancel
        </button>
        <button
          type="button"
          className="rounded-lg text-gray-900 bg-text-link text-sm tracking-wider font-medium outline-none hover:bg-gray-100 px-10 py-2"
          onClick={sendData}
        >
          Select
        </button>
      </div>
    </>
  );
};
